import {
	ReactElement,
	useCallback,
	useEffect,
	useState,
} from "react";
import { BusyIndicator } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	convertToBillingFormData,
	convertToCustomerInfoSaveModel,
	EMPTY_BILLING_FORM_DATA,
	validationConfiguration
} from "../Settings/Licensing/Billing/Page";
import {
	BillingFormData,
	CustomerSectionForm,
	getSubIndustriesOfIndustry,
	isIndustryWithSubIndustries,
} from "../Settings/Licensing/Billing/CustomerSection";
import {
	BillingViewModel,
	CustomerInfoSaveModel,
	Industry,
	load,
	saveCustomerInfo
} from "client/Api/Licensing/Billing/billing";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { InputValidationResult, InputValidationResultKind } from "client/Validation";
import { WelcomeStep } from "./WelcomeStep/WelcomeStep";
import { ContentLayout } from "./ContentLayout";
import image from "Content/Images/billing-step.svg";
import { PermissionChangeErrorMessage } from "./PermissionChangeErrorMessage";
import { HttpRequestError } from "client/Api/httpClient";
import { isNilOrWhiteSpace, isNotNil } from "client/Misc/utils";

export type BillingStepProps = {
	onNext(): void;
};

export const BillingStep = (props: BillingStepProps): ReactElement => {
	const { onNext } = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<BillingFormData>(EMPTY_BILLING_FORM_DATA);
	const [isEverythingRequiredAlreadyFilledIn, setIsEverythingRequiredAlreadyFilledIn] = useState<boolean>(false);

	const formHandler: IFormHandler<BillingFormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			try {
				const validationResult: InputValidationResult = await formHandler.validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				const saveModel: CustomerInfoSaveModel = convertToCustomerInfoSaveModel(
					formData
				);

				await saveCustomerInfo(saveModel, globalCancellation);

				setFormData(
					convertToBillingFormData(saveModel)
				);

				setIsBusy(false);
				onNext();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				let message: string | ReactElement | null = null;

				if (e instanceof HttpRequestError) {
					switch(e.statusCode) {
						case 403:
							message = <PermissionChangeErrorMessage />;
							break;
						default:
							if (!formHandler.validation.tryProcessServerError(e)) {
								message = localize("_GeneralErrorMessage");
							}
							break;
					}
				}

				if (message) {
					alertManager.showError(message);
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy,
			onNext
		]
	);

	useEffect(
		() => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				setIsBusy(true);

				try {
					const model: BillingViewModel = await load(signal);

					const billingFormData: BillingFormData = convertToBillingFormData(
						model.customerInfo
					);

					setFormData(billingFormData);

					setIsEverythingRequiredAlreadyFilledIn(
						isEverythingRequiredFilledIn(billingFormData)
					);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return (): void => cancellation.abort();
		},
		[alertManager, onNext]
	);

	return (
		<>
			<WelcomeStep.Left trackingId="billing-step">
				{isBusy && (
					<BusyIndicator/>
				)}

				<ContentLayout
					title={localize("_CompanyInformation")}
					explanation={localize("_BillingStepExplanation")}
				>
					<CustomerSectionForm
						attachable={formHandler}
					/>

					<ContentLayout.Buttons
						onContinue={saveHandler}
						onSkip={
							isEverythingRequiredAlreadyFilledIn
								? onNext
								: undefined
						}
					/>
				</ContentLayout>

			</WelcomeStep.Left>

			<WelcomeStep.Right
				imageUrl={image}
				text={localize("_TheWorkspaceOffersDifferentFeaturePackages")}
			/>
		</>
	);
};

BillingStep.displayName = "BillingStep";

const billingStaticPropertyToRequiredMap: {
	[key in StringKeyOf<BillingFormData>]: boolean;
} = {
	["name"]: true,
	["referenceNumber"]: false,
	["customerType"]: true,
	["industry"]: true,
	["employeeSize"]: true,
	["environmentType"]: true,
	["otherIndustry"]: false,
	["subIndustries"]: false,
	["industriesWithOtherSelected"]: false
};

export function isEverythingRequiredFilledIn(model: BillingFormData): boolean {
	const isStaticBillingInfoComplete: boolean = Object.entries(billingStaticPropertyToRequiredMap)
		.every(([key, value]) => {
			const typedKey = key as keyof typeof billingStaticPropertyToRequiredMap;

			return value === true
				? model[typedKey] !== EMPTY_BILLING_FORM_DATA[typedKey]
				: true;
		});

	if (
		isNotNil(model.industry)
		&& isIndustryWithSubIndustries(model.industry)
		&& isNotNil(model.subIndustries)
		&& getSubIndustriesOfIndustry(
			model.industry,
			model.subIndustries
		).length === 0
	) {
		return false;
	}

	if (model.industry === Industry.Other && isNilOrWhiteSpace(model.otherIndustry)) {
		return false;
	}

	return isStaticBillingInfoComplete;
}
